import React from 'react';
import styled from '@emotion/styled';
import { theme } from 'common/theme';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { chartPalette } from 'theme/colours';
import { useClickOutside } from 'common/hooks/useClickOutside';

const ColorInput = styled.input`
  padding: ${theme.space(1.5)};
  border: 1px solid ${theme.colors.borderGrey.toString()};
  border-radius: 6px;
  width: 120px;
  cursor: pointer;
  color: ${theme.v2.colours.gray900};
  background: white;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.v2.colours.latana};
  }
`;

const PopoverPicker = styled.div<{ useFixedPosition?: boolean }>`
  position: ${props => (props.useFixedPosition ? 'fixed' : 'absolute')};
  ${props =>
    props.useFixedPosition
      ? ``
      : `
    left: 0;
  `}
  top: 100%;
  z-index: 100000;
  background: white;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: ${theme.space(2)};
  margin-top: ${theme.space(1)};
`;

const Container = styled.div`
  position: relative;
`;

const PresetColors = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-top: ${theme.space(2)};
`;

const PresetColor = styled.button<{ color: string }>`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid ${theme.colors.borderGrey.toString()};
  background-color: ${props => props.color};
  cursor: pointer;
  padding: 0;

  &:hover {
    transform: scale(1.1);
  }
`;

const StyledHexInput = styled(HexColorInput)`
  width: 100%;
  margin-top: ${theme.space(1)};
  padding: ${theme.space(0.5)};
  border: 1px solid ${theme.colors.borderGrey.toString()};
  border-radius: 4px;
`;

interface ColorPickerInputProps {
  color: string;
  onChange: (color: string) => void;
  className?: string;
  useFixedPosition?: boolean;
}

const ColorPickerInput: React.FC<ColorPickerInputProps> = ({ color, onChange, className, useFixedPosition = false }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const pickerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  useClickOutside(pickerRef, () => setIsOpen(false));

  React.useEffect(() => {
    if (isOpen && useFixedPosition && inputRef.current && pickerRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      pickerRef.current.style.top = `${rect.bottom + 8}px`;
      pickerRef.current.style.left = `${rect.left}px`;
    }
  }, [isOpen, useFixedPosition]);

  return (
    <Container className={className}>
      <ColorInput ref={inputRef} type='text' value={color} onClick={() => setIsOpen(true)} readOnly />
      {isOpen && (
        <PopoverPicker ref={pickerRef} onClick={e => e.stopPropagation()} useFixedPosition={useFixedPosition}>
          <HexColorPicker color={color} onChange={onChange} />
          <StyledHexInput color={color} onChange={onChange} />
          <PresetColors>
            {chartPalette.slice(0, 10).map(presetColor => (
              <PresetColor key={presetColor} color={presetColor} onClick={() => onChange(presetColor)} />
            ))}
          </PresetColors>
        </PopoverPicker>
      )}
    </Container>
  );
};

export default ColorPickerInput;
